import React, { useState, useEffect, useCallback } from 'react';
import {
  MapPin, Navigation, RefreshCw, Save, CheckCircle,
  AlertTriangle, Info, ArrowLeft, Crosshair, Globe,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useFacility } from '../context/FacilityContext';
import { useAppRefresh } from '../hooks/useAppRefresh';
import { facilitiesAPI, onboardingAPI } from '../services/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OnboardingRibbon from '../features/home/OnboardingRibbon';
import { ONBOARDING_STEPS, onboardingHref } from '../features/home/onboardingSteps';

const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org';

async function reverseGeocode(lat, lng) {
  const url = `${NOMINATIM_BASE}/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=en`;
  const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
  if (!res.ok) throw new Error('Reverse geocoding failed');
  return res.json();
}

function StatusBanner({ facility }) {
  const hasCoords = facility?.latitude != null && facility?.longitude != null;
  if (hasCoords) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4">
        <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
        <div>
          <p className="text-sm font-medium text-green-800">Location is set</p>
          <p className="text-xs text-green-700 mt-0.5">
            {parseFloat(facility.latitude).toFixed(6)}, {parseFloat(facility.longitude).toFixed(6)}
            {facility.city ? ` · ${facility.city}` : ''}
          </p>
          <p className="text-xs text-green-600 mt-1">
            Weather widget will use precise coordinates for this ground.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
      <div>
        <p className="text-sm font-medium text-amber-800">Location not set yet</p>
        <p className="text-xs text-amber-700 mt-0.5">
          Use your mobile phone at the ground to get the most accurate coordinates.
          Weather will fall back to city name until coordinates are saved.
        </p>
      </div>
    </div>
  );
}

const FacilityLocation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inOnboarding = searchParams.get('onboarding') === '1';
  const { activeFacility, activeFacilityId, updateActiveFacilityLocation } = useFacility();
  const { refreshApp } = useAppRefresh();

  const [form, setForm] = useState({
    latitude: '',
    longitude: '',
    address: '',
    city: '',
    state: '',
  });
  const [accuracy, setAccuracy] = useState(null);
  const [gpsState, setGpsState] = useState('idle'); // idle | loading | success | denied | unavailable | error
  const [geocodeState, setGeocodeState] = useState('idle'); // idle | loading | success | error
  const [saveState, setSaveState] = useState('idle'); // idle | loading | success | error
  const [saveError, setSaveError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Pre-fill from existing facility data
  useEffect(() => {
    if (activeFacility) {
      setForm((prev) => ({
        ...prev,
        latitude: activeFacility.latitude != null ? String(activeFacility.latitude) : '',
        longitude: activeFacility.longitude != null ? String(activeFacility.longitude) : '',
        city: activeFacility.city || '',
      }));
    }
  }, [activeFacility]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: '' }));
    setSaveState('idle');
  };

  const doReverseGeocode = useCallback(async (lat, lng) => {
    setGeocodeState('loading');
    try {
      const data = await reverseGeocode(lat, lng);
      const addr = data.address || {};
      const city =
        addr.city || addr.town || addr.village || addr.county || addr.state_district || '';
      const state = addr.state || '';
      const road = [addr.road, addr.suburb, addr.neighbourhood].filter(Boolean).join(', ');
      setForm((prev) => ({
        ...prev,
        address: road || data.display_name?.split(',')[0] || '',
        city,
        state,
      }));
      setGeocodeState('success');
    } catch {
      setGeocodeState('error');
    }
  }, []);

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      setGpsState('unavailable');
      return;
    }
    setGpsState('loading');
    setAccuracy(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude.toFixed(7);
        const lng = pos.coords.longitude.toFixed(7);
        setAccuracy(Math.round(pos.coords.accuracy));
        setForm((prev) => ({ ...prev, latitude: lat, longitude: lng }));
        setGpsState('success');
        doReverseGeocode(lat, lng);
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) setGpsState('denied');
        else setGpsState('error');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const validate = () => {
    const errs = {};
    const lat = parseFloat(form.latitude);
    const lng = parseFloat(form.longitude);
    if (!form.latitude) errs.latitude = 'Latitude is required';
    else if (isNaN(lat) || lat < -90 || lat > 90) errs.latitude = 'Must be between -90 and 90';
    if (!form.longitude) errs.longitude = 'Longitude is required';
    else if (isNaN(lng) || lng < -180 || lng > 180) errs.longitude = 'Must be between -180 and 180';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaveState('loading');
    setSaveError('');
    try {
      const payload = {
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        ...(form.city.trim() && { city: form.city.trim() }),
        ...(form.state.trim() && { state: form.state.trim() }),
        ...(form.address.trim() && { address: form.address.trim() }),
      };
      const res = await facilitiesAPI.updateLocation(activeFacilityId, payload);
      if (!res.success) throw new Error(res.message || 'Save failed');
      updateActiveFacilityLocation(payload);
      await refreshApp();
      setSaveState('success');
      // Auto-advance onboarding to next step after location is saved
      if (inOnboarding) {
        try {
          const status = await onboardingAPI.getStatus();
          if (status?.success) {
            const steps = status.data.steps || {};
            const next = ONBOARDING_STEPS.find((s) => !steps[s.id] && s.id !== 'location');
            if (next) { navigate(onboardingHref(next)); return; }
          }
        } catch (_) {}
      }
      setTimeout(() => setSaveState('idle'), 3000);
    } catch (err) {
      setSaveError(err.message || 'Failed to save location. Please try again.');
      setSaveState('error');
    }
  };

  const gpsButtonLabel = {
    idle: 'Get My Current Location',
    loading: 'Getting location…',
    success: 'Location captured — tap again to refresh',
    denied: 'Permission denied — tap to retry',
    unavailable: 'GPS not available on this device',
    error: 'Could not get location — tap to retry',
  }[gpsState];

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        {inOnboarding && <OnboardingRibbon />}
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="mb-4 flex items-center gap-2 text-ga-text-muted hover:text-ga-text-primary transition-colors text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <h1 className="text-2xl font-bold text-ga-text-primary flex items-center gap-2">
            <MapPin className="h-6 w-6 text-ga-primary" />
            Ground Location
          </h1>
          <p className="text-sm text-ga-text-muted mt-1">
            {activeFacility?.facility_name || 'Your Facility'}
          </p>
        </div>

        {/* Current status */}
        <div className="mb-6">
          <StatusBanner facility={activeFacility} />
        </div>

        {/* Step 1 — GPS */}
        <div className="ga-card rounded-xl p-6 mb-4">
          <h2 className="text-base font-semibold text-ga-text-primary mb-1 flex items-center gap-2">
            <Crosshair className="h-5 w-5 text-ga-primary" />
            Step 1 — Capture Location from This Device
          </h2>
          <p className="text-sm text-ga-text-muted mb-4">
            Open this page on your mobile phone while standing at the cricket ground, then tap the button below for the most accurate result.
          </p>

          <button
            onClick={handleGetLocation}
            disabled={gpsState === 'loading'}
            className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium text-sm transition-colors
              ${gpsState === 'loading'
                ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                : gpsState === 'success'
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : gpsState === 'denied' || gpsState === 'error'
                ? 'bg-amber-500 hover:bg-amber-600 text-white'
                : 'bg-ga-primary hover:bg-green-700 text-white'
              }`}
          >
            {gpsState === 'loading' ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : gpsState === 'success' ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <Navigation className="h-4 w-4" />
            )}
            {gpsButtonLabel}
          </button>

          {accuracy != null && gpsState === 'success' && (
            <p className="mt-2 text-xs text-green-700 text-center">
              Accuracy: ±{accuracy} metres
            </p>
          )}

          {gpsState === 'denied' && (
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3">
              <Info className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-700">
                Location access was denied. Please enable it in your browser settings
                (Settings → Privacy → Location → Allow for this site) and try again.
              </p>
            </div>
          )}

          {gpsState === 'unavailable' && (
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-200 p-3">
              <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
              <p className="text-xs text-blue-700">
                GPS is not available on this device. Please open this page on a mobile phone at the ground, or enter coordinates manually below.
              </p>
            </div>
          )}
        </div>

        {/* Step 2 — Coordinates & address */}
        <div className="ga-card rounded-xl p-6 mb-4">
          <h2 className="text-base font-semibold text-ga-text-primary mb-1 flex items-center gap-2">
            <Globe className="h-5 w-5 text-ga-primary" />
            Step 2 — Review &amp; Edit Details
          </h2>
          <p className="text-sm text-ga-text-muted mb-4">
            Fields are auto-filled after capturing GPS. You can also paste coordinates from Google Maps (right-click on the ground → Copy coordinates).
          </p>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Latitude <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="any"
                value={form.latitude}
                onChange={(e) => handleChange('latitude', e.target.value)}
                placeholder="e.g. 28.6139275"
                className={`input-field w-full text-sm ${fieldErrors.latitude ? 'border-red-400' : ''}`}
              />
              {fieldErrors.latitude && (
                <p className="text-xs text-red-500 mt-1">{fieldErrors.latitude}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Longitude <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="any"
                value={form.longitude}
                onChange={(e) => handleChange('longitude', e.target.value)}
                placeholder="e.g. 77.2090212"
                className={`input-field w-full text-sm ${fieldErrors.longitude ? 'border-red-400' : ''}`}
              />
              {fieldErrors.longitude && (
                <p className="text-xs text-red-500 mt-1">{fieldErrors.longitude}</p>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Address{' '}
                {geocodeState === 'loading' && (
                  <span className="text-ga-text-muted font-normal">(detecting…)</span>
                )}
              </label>
              <input
                type="text"
                value={form.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="Street address (auto-filled from GPS)"
                className="input-field w-full text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">City</label>
                <input
                  type="text"
                  value={form.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="City"
                  className="input-field w-full text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
                <input
                  type="text"
                  value={form.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  placeholder="State"
                  className="input-field w-full text-sm"
                />
              </div>
            </div>
          </div>

          {geocodeState === 'error' && (
            <p className="mt-3 text-xs text-amber-600">
              Could not auto-detect address — please fill in City and State manually.
            </p>
          )}
        </div>

        {/* Save */}
        {saveError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-700">{saveError}</p>
          </div>
        )}

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saveState === 'loading' || saveState === 'success'}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium text-sm transition-colors
              ${saveState === 'success'
                ? 'bg-green-600 text-white cursor-default'
                : saveState === 'loading'
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-ga-primary hover:bg-green-700 text-white'
              }`}
          >
            {saveState === 'loading' ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : saveState === 'success' ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saveState === 'success' ? 'Saved!' : saveState === 'loading' ? 'Saving…' : 'Save Location'}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FacilityLocation;
